export type Theme = {
  slug: string;
  title: string;
  titlePt: string;
  seoTitle: string;
  description: string;
  intro: string;
  introPt?: string;
  /** Short topic line shown under the title. */
  keywords?: string;
  /** Links to texts about the theme (essays, articles); the section shows once filled. */
  texts?: { label: string; href: string }[];
  projects: string[];
};

export const themes: Theme[] = [
  {
    slug: "decolonial-art",
    title: "Decolonial Art",
    titlePt: "Arte decolonial",
    seoTitle: "Decolonial Art — Luiza Jesus do Prado",
    description:
      "Decolonial art by Brazilian artist Luiza Jesus do Prado: performance, photography and installation on colonization, necropolitics, Indigenous knowledge and the body.",
    intro:
      "Luiza Jesus do Prado is a Brazilian political artist whose work confronts colonial structures through the body. Performance, photography and installation address colonized sexuality, necropolitics, State violence and Indigenous knowledge, proposing ritual and fiction as forms of resistance.",
    introPt:
      "Luiza Jesus do Prado é uma artista brasileira de arte política e decolonial. Sua obra enfrenta as estruturas coloniais a partir do corpo — em performance, fotografia e instalação — tratando de sexualidade colonizada, necropolítica, violência de Estado e saberes indígenas, com o ritual e a ficção como formas de resistência.",
    projects: [
      "colonized-sexuality",
      "discourses-of-necropolitics",
      "topographies-of-violence",
      "o-estado",
      "butoh-caipira",
      "appropriation",
      "border",
      "resistencia",
      "e-dai",
      "the-painter",
    ],
  },
  {
    slug: "art-science-technology",
    title: "Art, Science & Technology",
    titlePt: "Arte, ciência e tecnologia",
    seoTitle: "Art, Science & Technology — Luiza Jesus do Prado",
    description:
      "Art, science and technology by Luiza Jesus do Prado: patented apparatus Medusa (INPI), wearable sound instruments, perception and memory research.",
    intro:
      "Working between the studio and the laboratory, Luiza Jesus do Prado develops devices, instruments and research on perception and memory, drawing on data science to turn image and sound into material for investigation. Her apparatus Medusa holds an invention patent from INPI (Brazil), and works such as Body Sound and Ecologies of Perception turn the body into a site of scientific and sensory inquiry.",
    introPt:
      "Entre o ateliê e o laboratório, Luiza Jesus do Prado desenvolve aparelhos, instrumentos e pesquisas sobre percepção e memória, recorrendo à ciência de dados para transformar imagem e som em material de investigação. O aparelho Medusa tem patente de invenção pelo INPI, e obras como Body Sound e Ecologies of Perception fazem do corpo um campo de investigação científica e sensorial.",
    projects: [
      "the-weight-of-feeling",
      "neurotransition",
      "body-sound",
      "ecologies-of-perception",
      "apophenia-and-pareidolia",
      "diary-of-memories-that-i-dont-have",
      "symbiogenesis",
      "camara-obscura",
    ],
  },
  {
    slug: "decolonization-mental-health",
    title: "Decolonization and Mental Health",
    titlePt: "Descolonização e saúde mental",
    seoTitle: "Decolonization and Mental Health — Luiza Jesus do Prado",
    description:
      "Art on decolonization and mental health by Luiza Jesus do Prado: colonial trauma, memory, madness, necropolitics, the anti-asylum struggle and ancestral healing.",
    intro:
      "Mental health runs through Luiza Jesus do Prado's practice as a political and decolonial question. Her projects approach madness, trauma, memory and altered states not as pathology but as knowledge — linking the history of psychiatry, the anti-asylum struggle, public healthcare, spirituality and ancestral healing.",
    keywords: "Colonial trauma · Memory · Madness · Necropolitics · Anti-asylum struggle · Ancestral healing",
    texts: [],
    projects: [
      "what-is-madness",
      "diary-of-memories-that-i-dont-have",
      "neurotransition",
      "fungi",
      "entities",
      "entities-2",
      "symbiogenesis",
      "o-passaro-agoniza",
      "garden-of-dreams-and-nightmares-situated-between-the-loom",
      "reincarnate",
    ],
  },
  {
    slug: "sexuality-gender",
    title: "Eroticism, Dissidence and the Economy of the Body",
    titlePt: "Erotismo, dissidência e economia do corpo",
    seoTitle: "Sexuality and Gender in Art — Luiza Jesus do Prado",
    description:
      "Art on sexuality and gender by Luiza Jesus do Prado: colonized sexuality, dissident bodies, sex work, the economy of desire and the decolonization of the body.",
    intro:
      "Sexuality and gender appear in Luiza Jesus do Prado's work as territories disputed by colonial, religious and market norms. Performance, photography and installation address colonized sexuality, dissident and nonconforming bodies, sex work, the economy of desire and the decolonization of the body.",
    keywords: "Sexuality and Gender · Economy of desire · Decolonization of the body",
    texts: [],
    projects: [
      "colonized-sexuality",
      "fetiche",
      "appropriation",
      "corpo-estranho",
      "devir",
      "topographies-of-violence",
      "neurotransition",
    ],
  },
];

export const getTheme = (slug: string) => themes.find((t) => t.slug === slug);

/** Former addresses that now live under another slug. */
export const themeRedirects: Record<string, string> = {
  "mental-health-ritual-psilocybin": "decolonization-mental-health",
};
